import { useMemo } from 'react';
import { combineLatest, distinctUntilChanged, map, type Observable, shareReplay, startWith } from 'rxjs';
import type { ErrorSubscriptionResponse } from '../types';
import { useShallowArrayMemo } from './useShallowArrayMemo';

/**
 * If you're doing some dope ws stream merging, and you want to render potential error from those streams in your blotter, you've come to the right place
 * Just give me your flat array of error observables, don't need to memoize, and I will give you back an error stream as if you just had
 * one subscription
 *
 * There are some caveats on which error is emitted in the case of several, so take a look at that if you are interested
 */
export const useCombinedErrorObs = (errorObservables: Observable<ErrorSubscriptionResponse | undefined>[]) => {
  const memoizedErrorObs = useShallowArrayMemo(errorObservables);
  return useMemo(() => {
    // Start them all immediately with undefined. Then we do a distinctUntilChanged because useSubscription can spam "undefined" a lot
    // This is just good practice too I feel
    const mappedIndividualErrorObs = memoizedErrorObs.map(obs =>
      obs.pipe(startWith(undefined), distinctUntilChanged())
    );
    return combineLatest(mappedIndividualErrorObs).pipe(
      // Here we have an array like [undefined, undefined, error, error] (for example)
      // We just grab the first error we find. That is good enough for now.
      map(errors => {
        return errors.find(e => e?.error);
      }),
      shareReplay(1)
    );
  }, [memoizedErrorObs]);
};
